/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from "../../../../redux/hooks";
import {vehicleProgramSlice} from "../../../../redux/ducks/Wizard/slices";
import WithWizardProvider from "../../../components/Wizard/WithWizardProvider";
import CloseGlobalDialogButton from "../../../components/Global/Dialog/CloseGlobalDialogButton";
import {Toolbar, Typography} from '@mui/material';
import {usePostRequest} from "../../../../api/helper";
import {VEHICLE_PROGRAMS_CONSTANTS} from "../constants";
import {toast} from "react-hot-toast";
import ArgosFlexbox from "../../../components/Containers/ArgosFlexbox";
import VehicleProgramWizard from "../Wizard/VehicleProgramWizard";
import {
  EBluelineReportStatusMapRequestTypes,
  selectVehicleProgramsMainData,
  setDevelopmentStagesTree,
  setItemDetailsMainData,
  setReportGenerationStatusMap,
  updateReportStatusMap
} from "../../../../redux/ducks/VehicleProgramsDetails/slice";
import {useSelectWizardOpenc, useSliceActions} from "../../../../redux/ducks/Wizard/SliceProvider";
import {useLocation, useSearchParams} from "react-router-dom";
import {selectGlobalDialogState} from "../../../../redux/ducks/GlobalDialog/slice";
import ArgosExpandableContainer from '../../../components/Containers/ArgosExpandableContainer';
import useGetHubName from "../../../components/Socket/useGetHubName";
import useSocket from "../../../components/Socket/useSocket";
import LeftPanel from "./LeftPanel";
import RightPanel from "./RightPanel/RightPanel";


const VehicleProgramItemDetails = () => {
  const dispatch = useAppDispatch()
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParams = Object.fromEntries(searchParams)
  const paramobj: any = useAppSelector(state => {
    const dialogState = selectGlobalDialogState(state)
    if (dialogState.isOpen) {
      return dialogState.component?.params ?? urlParams
    } else return urlParams
  })
  const [vehicleProgramId, setVehicleProgramId] = useState<string | null>(paramobj?.vehicleProgramId ?? null)
  useEffect(() => {
    setVehicleProgramId(paramobj?.vehicleProgramId ?? null)
  }, [paramobj?.vehicleProgramId]);

  /**
   * Data from the landing page item
   */
  const mainVehicleProgramData = useAppSelector(selectVehicleProgramsMainData)
  const programCode = mainVehicleProgramData?.program_code
  const modelYear = mainVehicleProgramData?.model_year

  /**
   * Fetches tree and main data
   */
  const fetchItemDetailsData = usePostRequest(VEHICLE_PROGRAMS_CONSTANTS.itemDetails.endpoints.getVehicleProgramItemDetailsData)
  const [loadingMainData, setLoadingMainData] = React.useState(false)

  useEffect(() => {
    if (!vehicleProgramId) return
    setLoadingMainData(true)
    fetchItemDetailsData({vehicleProgramId})
      .then((res: any) => {
        const mainData = res?.vehicleProgram
        dispatch(setItemDetailsMainData(mainData))
        const tree = res?.tree
        dispatch(setDevelopmentStagesTree(tree))
      })
      .catch((err: any) => {
        toast.error('Error fetching tree')
        console.log('error', err)
      })
      .finally(() => {
        setLoadingMainData(false)
      })
  }, [vehicleProgramId]);

  /**
   * Wizard
   */
  const {setWizardOpen, setWizardMetaData} = useSliceActions()
  const isWizardOpen = useSelectWizardOpenc()
  useEffect(() => {
    if (!isWizardOpen) return
    const wizardMetaData = {
      'program_code': programCode,
      'model_year': modelYear,
      wizardType: 'Vehicle Program',
      vehicleProgramId
    }
    dispatch(setWizardMetaData(wizardMetaData))

  }, [programCode, modelYear, vehicleProgramId, isWizardOpen]);
  useEffect(() => {
    if (isWizardOpen) return
    // Leaving here until we can verify blob storage ttl rule works
    // checkAndDeleteOrphanFiles()
  }, [isWizardOpen]);

  const onMessage = useCallback((msg: any) => {
    const data = msg.data
    const type = msg.type

    switch (type) {
      case 'updateReportStatus': {
        // Store a redux map of developmentStageId to map of redlineDatasetId to status. Socket messages will send developmentStageId and map of redlineDatasetId to status
        const dispatchPayload = {
          data,
          requestType: EBluelineReportStatusMapRequestTypes.UPDATE
        }
        dispatch(updateReportStatusMap(dispatchPayload))
        break
      }
      case 'updateBluelineGenerationStatus': {
        const {developmentStageId, reportGenerationStatusObject} = data
        const reportGenerationStatusObjectMap = {
          [developmentStageId]: reportGenerationStatusObject
        }
        const dispatchData = {
          reportGenerationStatusObjectMap,
        }
        const dispatchPayload = {
          data: dispatchData,
          requestType: 'update'
        }
        dispatch(setReportGenerationStatusMap(dispatchPayload))
        break
      }
    }
  }, [])
  const hubName = useGetHubName(`vehicleProgramsReports_${vehicleProgramId}`)
  useSocket(hubName, onMessage)
  useEffect(() => {
    if (programCode)
      document.title = VEHICLE_PROGRAMS_CONSTANTS.landing.title + ' - ' + programCode;
    return () => {
      document.title = VEHICLE_PROGRAMS_CONSTANTS.landing.title + ' - Landing';
    };
  }, [programCode]);
  return (
    <ArgosFlexbox vertical style={{height: "100%", width: '100%', backgroundColor: 'white', overflow: 'auto'}}>
      <div style={{}}>
        <div style={{}}>
          <Toolbar variant="dense" style={{display: "flex", justifyContent: "space-between", background: '#3481d5'}}>
            <Typography
              sx={{
                color: '#ffffff',
                fontSize: 14,
                width: "230px",
                marginTop: 0.5
              }}>
              {
                (programCode && modelYear) ?
                  `${programCode}-${modelYear}` : ''
              }
            </Typography>
            <CloseGlobalDialogButton/>
          </Toolbar>
        </div>
      </div>
      <div style={{overflow: 'auto', flex: 1}}>
        <ArgosExpandableContainer initialLeftSize={'40%'} leftSide={<LeftPanel setWizardDialogOpen={setWizardOpen} loadingMainData={loadingMainData}/>} rightSide={<RightPanel/>}/>
      </div>

      <VehicleProgramWizard id={'currentVersionId'} versionNumber={1} programCode={programCode}/>
    </ArgosFlexbox>
  );
}

export default WithWizardProvider({slice: vehicleProgramSlice})(VehicleProgramItemDetails)
